import { createContext } from '@shared/utils/createContext';
import React, { useMemo } from 'react';
import { useHistory } from '@shared/core';
import { useLocation } from '@react-router';
import qs from 'query-string';
import { deriveActiveFiltersFromParam, deriveActiveFiltersFromUrl, FILTER_QS_PARSE_OPTIONS } from './utils/deriveActiveFiltersFromUrl';

export const convertObjectToQueryParams = (filters: { [facet: string]: string[] }) => {
  return qs.stringify(filters, FILTER_QS_PARSE_OPTIONS);
};

type QueryStringOptions = Readonly<{
  clearFormat?: boolean;
}>;

const getQueryStringFromParams = (currentUrl: string, facetGroupMap: Record<string, string[]>, options: QueryStringOptions = {}) => {
  const filterAsQueryParam = convertObjectToQueryParams(facetGroupMap);

  // Merge by hand rather than leaning on `skipNull`/`skipEmptyString`. Both flags
  // apply to the whole merged query, and `parseUrl` represents a valueless param
  // (`?showSearchScores`) as `null` — so `skipNull` would silently strip any such
  // param already on the URL. Here we only remove the filter keys this provider owns.
  const { url, query, fragmentIdentifier } = qs.parseUrl(currentUrl, { parseFragmentIdentifier: true });
  if (options.clearFormat) delete query.format;
  if (filterAsQueryParam) {
    query.pf = filterAsQueryParam;
  } else {
    delete query.pf;
  }

  return qs.stringifyUrl({ url, query, fragmentIdentifier }, { encode: true });
};

const toFacetGroupMap = (compositeCodes: Iterable<string>) => {
  const facetGroupMap = {} as Record<string, string[]>;
  for (const filterCompositeCode of compositeCodes) {
    const [groupCode, valueCode] = filterCompositeCode.split(':');
    facetGroupMap[groupCode] = facetGroupMap[groupCode] || [];
    facetGroupMap[groupCode].push(valueCode);
  }
  return facetGroupMap;
};

type DesignGalleryFiltersProviderContext = {
  activePrintFilters: ReadonlySet<string>;
  clearAllFilters: () => void;
  /** Number of active `pf` facets. The `?format=` display param is not a filter and is not counted. */
  getTotalActiveFilterCount: () => number;
  /**
   * Switches one facet option and returns the selection it pushed, so a caller deriving
   * anything from the result doesn't have to re-read a render-stale `activePrintFilters`.
   */
  setActiveFilter(compositeCode: string, checked: boolean): ReadonlySet<string>;
  /**
   * Replace every active filter in `groupCode` with `valueCodes` in a single
   * history entry. Prefer this over looping `setActiveFilter`: that reads the
   * live URL each call, which `history.push` has not yet updated, so only the
   * last call survives — and each one still costs a back-button press.
   */
  setActiveFilterGroup(groupCode: string, valueCodes: readonly string[]): void;
};
const [Provider, useDesignGalleryFiltersContext] = createContext<DesignGalleryFiltersProviderContext>({ name: 'StationeryFiltersProvider' });

export const DesignGalleryFiltersProvider: React.FC = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const productFiltersParam = qs.parse(location.search).pf;
  const filterSelection = typeof productFiltersParam === 'string' ? productFiltersParam : undefined;
  // Unrelated query parameters must not invalidate downstream template filtering.
  const activePrintFilters = useMemo(() => deriveActiveFiltersFromParam(filterSelection), [filterSelection]);

  const ctx = useMemo<DesignGalleryFiltersProviderContext>(() => {
    const getCurrentUrl = () => {
      const currentLocation = history.location;
      return `${currentLocation.pathname}${currentLocation.search}${currentLocation.hash}`;
    };

    const getCurrentFilters = () => deriveActiveFiltersFromUrl(getCurrentUrl());

    return {
      activePrintFilters,
      clearAllFilters() {
        history.push(getQueryStringFromParams(getCurrentUrl(), {}, { clearFormat: true }));
      },
      getTotalActiveFilterCount() {
        return activePrintFilters.size;
      },
      setActiveFilter(compositeCode: string, checked: boolean) {
        const clonedActivePrintFilters = new Set(getCurrentFilters());
        if (checked) {
          clonedActivePrintFilters.add(compositeCode);
        } else {
          clonedActivePrintFilters.delete(compositeCode);
        }

        history.push(getQueryStringFromParams(getCurrentUrl(), toFacetGroupMap(clonedActivePrintFilters)));
        return clonedActivePrintFilters;
      },
      setActiveFilterGroup(groupCode: string, valueCodes: readonly string[]) {
        const next = new Set(Array.from(getCurrentFilters()).filter(compositeCode => !compositeCode.startsWith(`${groupCode}:`)));
        for (const valueCode of valueCodes) {
          next.add(`${groupCode}:${valueCode}`);
        }

        history.push(getQueryStringFromParams(getCurrentUrl(), toFacetGroupMap(next)));
      },
    };
  }, [activePrintFilters, history]);
  return <Provider value={ctx}>{children}</Provider>;
};

export { useDesignGalleryFiltersContext };
